import { useFormik } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';

import { UNAUTHORIZED_MESSAGE } from '../constants';
import { SSN_REGEX } from '../utilities/formatting';
import { logError } from '../utilities/logging';

import { InputReentryCard } from './Card';
import { SsnTextInput, SsnInputInfoCopy } from './SsnTextInput';

interface SsnReentryProps {
  onReenterSubmit: (ssn: string) => void;
}
interface SsnReentryFormValues {
  ssnReentry: string;
}

export const SsnReentryCard: FC<SsnReentryProps> = ({ onReenterSubmit }) => {
  const formik = useFormik({
    initialValues: {
      ssnReentry: '',
    },
    validationSchema: Yup.object().shape({
      ssnReentry: Yup.string()
        .trim()
        .min(9, 'Enter a valid 9-digit SSN')
        .max(9, 'Enter a valid 9-digit SSN')
        .matches(SSN_REGEX, 'Enter a valid SSN')
        .required('Enter your SSN'),
    }),
    onSubmit: async (values: SsnReentryFormValues) => {
      try {
        // Get validated SSN input value
        const { ssnReentry } = values;
        // Invoke upstream submit handler
        onReenterSubmit(ssnReentry);
      } catch (err: any) {
        if (err && err.message !== UNAUTHORIZED_MESSAGE) {
          logError(err, {
            errorName: err.name || 'ComponentError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'SsnReentryCard',
            function: 'onSubmit',
          });
        }
      }
    },
  });

  return (
    <InputReentryCard
      heading="Re-enter your SSN"
      subheading="Please double-check that your social security number is correct."
      fieldname="ssn"
      onReenterSubmit={formik.handleSubmit}
      infoAboveForm={SsnInputInfoCopy}
    >
      <SsnTextInput
        name="ssnReentry"
        value={formik.values.ssnReentry}
        label="Social security number"
        error={formik.errors.ssnReentry}
        touched={formik.touched.ssnReentry}
        disabled={formik.isSubmitting}
        onChange={formik.handleChange}
        setFieldTouched={() => formik.setFieldTouched('ssn')}
        onBlur={(e: React.FocusEvent<any>) => {
          formik.handleBlur(e);
          if (
            e &&
            e.target &&
            e.target.value === '' &&
            e.relatedTarget === document.getElementById('toggle')
          ) {
            // Focus Continue button when ssn input is blurred without a value
            // (workaround to support keyboard navigation in Firefox)
            const continueBtnId = 'ssn-reentry-submit-button';
            const continueButton = document.getElementById(continueBtnId);
            if (continueButton) continueButton.focus();
          }
        }}
        showInfoOnFocus={false}
      />
    </InputReentryCard>
  );
};
